// SSE-driven notifications. Replaces the 60-second `setInterval` poll
// that used to live in SidebarUserMenu.vue.
//
// The backend (`GET /notifications/events`) emits a `bootstrap` event
// with the full initial list, then live deltas as new notifications
// arrive or get marked read — including cross-tab updates, since
// every open tab subscribes to the same per-user channel.
//
// This composable owns the EventSource lifecycle and routes payloads
// into the Pinia store via `consumeEvent`. It mounts open, unmounts
// close, and has no return value.

import { onMounted } from 'vue'

import type { NotificationOut } from '@/api'
import { useSseStream } from '@/composables/useSseStream'
import { useVisibilityRefresh } from '@/composables/useVisibilityRefresh'
import { useNotificationsStore } from '@/stores/notifications'

export type NotificationStreamEvent =
  | { type: 'bootstrap'; notifications: NotificationOut[] }
  | { type: 'notification_created'; notification: NotificationOut }
  | { type: 'notification_updated'; notification: NotificationOut }
  | { type: 'notifications_all_read'; read_at: string }

export function useNotificationsStream(enabled = true): void {
  const notifications = useNotificationsStore()

  const sse = useSseStream<NotificationStreamEvent>({
    onEvent(event) {
      notifications.consumeEvent(event)
    },
  })

  onMounted(() => {
    if (enabled) sse.open('/notifications/events')
  })

  // Recovery: when the tab regains focus after a background gap (the server
  // closes idle SSE connections; reconnect can give up), revive a dead stream
  // and pull the authoritative list so the bell isn't stale until a reload.
  useVisibilityRefresh(() => {
    if (!enabled) return
    sse.reopenIfClosed()
    void notifications.fetchNotifications()
  })

  // useSseStream registers its own onUnmounted handler, so no manual
  // cleanup is needed here.
}
